<template>
  <div>

    <!-- Page Header -->
    <div class="page-header">
      <div class="page-header-left">
        <button class="back-btn" @click="goBack"><font-awesome-icon icon="arrow-left" /> Requisitions</button>
        <div>
          <h5 class="page-title">{{ isEdit ? 'Edit Requisition' : 'New Job Requisition' }}</h5>
          <p class="page-sub">{{ isEdit ? reqRef : 'Complete all sections to submit for approval' }}</p>
        </div>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table"><div v-for="n in 8" :key="n" class="sk-row"></div></div>

    <div v-else>

      <!-- ── Workforce Plan Linkage ─────────────────────────────────── -->
      <div class="form-section">
        <div class="section-head">
          <p class="section-title">Workforce Plan Linkage</p>
          <label class="toggle-label">
            <input type="checkbox" v-model="useWorkforcePlan" @change="onWFPToggle" />
            <span class="toggle-track"></span>
            <span class="toggle-text">Link to an Approved Workforce Plan</span>
          </label>
        </div>

        <div v-if="useWorkforcePlan" class="wfp-grid">
          <div class="form-row">
            <label class="form-label">Approved Workforce Plan <span class="req">*</span></label>
            <select class="form-input" v-model="form.workforce_plan_id" @change="onPlanChange">
              <option value="">— select plan —</option>
              <option v-for="p in approvedPlans" :key="p.id" :value="p.id">{{ p.reference }} — {{ p.name }}</option>
            </select>
          </div>
          <div class="form-row" v-if="form.workforce_plan_id">
            <label class="form-label">Role from Plan</label>
            <select class="form-input" v-model="selectedRoleId" @change="onRoleChange">
              <option value="">— select role —</option>
              <option v-for="r in planRoles" :key="r.id" :value="r.id">
                {{ r.position?.name || 'Role ' + (planRoles.indexOf(r)+1) }} ({{ r.employment_type }}, × {{ r.position_count }})
              </option>
            </select>
          </div>

          <div v-if="selectedRoleId && selectedRole" class="breakdown-card">
            <div class="breakdown-item"><span class="breakdown-label">Positions Requested</span><span class="breakdown-value">{{ selectedRole.position_count || 1 }}</span></div>
            <div class="breakdown-item"><span class="breakdown-label">Job Grade</span><span class="breakdown-value">{{ selectedRole.pay_grade?.name || '—' }}</span></div>
            <div class="breakdown-item"><span class="breakdown-label">Salary Band</span>
              <span class="breakdown-value" v-if="selectedRole.min_salary || selectedRole.max_salary">
                {{ fmt(selectedRole.min_salary) }} – {{ fmt(selectedRole.max_salary) }}
              </span>
              <span class="breakdown-value" v-else>—</span>
            </div>
          </div>
        </div>

        <div v-else class="info-box info-box--warn">
          <font-awesome-icon icon="exclamation-triangle" />
          <div>
            <strong>No Workforce Plan Selected</strong>
            <p style="margin:4px 0 0">This requisition does not have an approved workforce plan. A written justification is required and additional written approvals may be needed.</p>
          </div>
        </div>

        <div v-if="!useWorkforcePlan" class="form-row" style="margin-top:14px">
          <label class="form-label">Justification for Requisition without Plan <span class="req">*</span></label>
          <textarea class="form-input" rows="3" v-model="form.no_plan_justification" placeholder="Explain why this requisition is being raised without an approved workforce plan…"></textarea>
        </div>
      </div>

      <!-- ── Section A: Requisition Details ───────────────────────── -->
      <div class="form-section">
        <p class="section-title">Section A — Requisition Details</p>
        <div class="form-cols-3">
          <div class="form-row">
            <label class="form-label">Date of Request</label>
            <input type="date" class="form-input" v-model="form.date_of_request" />
          </div>
          <div class="form-row">
            <label class="form-label">Requesting Department <span class="req">*</span></label>
            <select class="form-input" v-model="form.department_id">
              <option value="">— select —</option>
              <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Departmental Cost Centre</label>
            <input type="text" class="form-input" v-model="form.departmental_cost_centre" placeholder="e.g. CC1001" />
          </div>
          <div class="form-row">
            <label class="form-label">Location</label>
            <select class="form-input" v-model="form.company_location_id">
              <option value="">— select —</option>
              <option v-for="l in locations" :key="l.id" :value="l.id">{{ l.name }}</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Employment Type</label>
            <select class="form-input" v-model="form.employment_type">
              <option value="">— select —</option>
              <option v-for="t in ['permanent','contract','intern','adhoc']" :key="t" :value="t">{{ ucFirst(t) }}</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Recruitment Type</label>
            <select class="form-input" v-model="form.recruitment_type">
              <option value="">— select —</option>
              <option value="new_hire">New Hire</option>
              <option value="replacement">Replacement</option>
              <option value="internal_transfer">Internal Transfer</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Reason for Vacancy</label>
            <select class="form-input" v-model="form.recruitment_reason">
              <option value="">— select —</option>
              <option value="new_position">New Position</option>
              <option value="backfill">Backfill / Replacement</option>
              <option value="expansion">Business Expansion</option>
              <option value="temporary">Temporary / Project</option>
            </select>
          </div>
          <div class="form-row" v-if="form.recruitment_reason === 'backfill'">
            <label class="form-label">Exiting Staff Name</label>
            <input type="text" class="form-input" v-model="form.exiting_staff" placeholder="Name of departing employee" />
          </div>
          <div class="form-row">
            <label class="form-label">Proposed Start Date</label>
            <input type="date" class="form-input" v-model="form.proposed_start_date" />
          </div>
        </div>
      </div>

      <!-- ── Section B: Position Information ──────────────────────── -->
      <div class="form-section">
        <p class="section-title">Section B — Position Information</p>
        <div v-if="selectedRoleId" class="info-box info-box--blue" style="margin-bottom:12px">
          <font-awesome-icon icon="info-circle" /> Fields pre-filled from the selected workforce plan role. Some fields are locked.
        </div>
        <div class="form-cols-3">
          <div class="form-row">
            <label class="form-label">Job Title / Position <span class="req">*</span></label>
            <select class="form-input" v-model="form.position_id" :disabled="!!selectedRoleId">
              <option value="">— select —</option>
              <option v-for="p in positions" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Job Code <span class="req">*</span></label>
            <input type="text" class="form-input" v-model="form.job_code" placeholder="e.g. ENG-2026-001" />
          </div>
          <div class="form-row">
            <label class="form-label">Job Grade</label>
            <select class="form-input" v-model="form.pay_grade_id" :disabled="!!selectedRoleId">
              <option value="">— select —</option>
              <option v-for="g in payGrades" :key="g.id" :value="g.id">{{ g.name }}</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Reporting Line</label>
            <select class="form-input" v-model="form.reporting_position_id">
              <option value="">— select —</option>
              <option v-for="p in positions" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Supervisory Responsibility</label>
            <div class="radio-group">
              <label class="radio-label"><input type="radio" :value="true" v-model="form.supervisory" /> Yes</label>
              <label class="radio-label"><input type="radio" :value="false" v-model="form.supervisory" /> No</label>
            </div>
          </div>
          <div class="form-row">
            <label class="form-label">Number of Positions <span class="req">*</span></label>
            <input type="number" class="form-input" v-model.number="form.position_count" min="1" :disabled="!!selectedRoleId" />
          </div>
          <div class="form-row">
            <label class="form-label">Work Arrangement</label>
            <select class="form-input" v-model="form.work_arrangement">
              <option value="">— select —</option>
              <option value="onsite">On-Site</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
        </div>
      </div>

      <!-- ── Section C: Justification ──────────────────────────────── -->
      <div class="form-section">
        <p class="section-title">Section C — Justification for Recruitment</p>
        <p class="form-label" style="margin-bottom:10px">Select all that apply:</p>
        <div class="checkbox-grid">
          <label v-for="opt in justificationOptions" :key="opt.value" class="checkbox-label">
            <input type="checkbox" :value="opt.value" v-model="form.justification_arr" />
            <span>{{ opt.label }}</span>
          </label>
        </div>
        <div class="form-row" style="margin-top:14px">
          <label class="form-label">Detailed Justification</label>
          <textarea class="form-input" rows="4" v-model="form.justification_detail" placeholder="Provide a detailed explanation for this recruitment need…"></textarea>
        </div>
      </div>

      <!-- ── Section D: Workforce & Budget Confirmation ─────────────── -->
      <div class="form-section">
        <p class="section-title">Section D — Workforce & Budget Confirmation</p>
        <div class="confirm-grid">
          <div class="confirm-row">
            <span class="confirm-question">Is this role covered in an approved workforce plan?</span>
            <div class="radio-group">
              <label class="radio-label"><input type="radio" :value="true" v-model="form.is_approved_workforce_plan" /> Yes</label>
              <label class="radio-label"><input type="radio" :value="false" v-model="form.is_approved_workforce_plan" /> No</label>
            </div>
          </div>
          <div class="confirm-row">
            <span class="confirm-question">Is this within the approved headcount establishment?</span>
            <div class="radio-group">
              <label class="radio-label"><input type="radio" :value="true" v-model="form.is_within_approved_establishment" /> Yes</label>
              <label class="radio-label"><input type="radio" :value="false" v-model="form.is_within_approved_establishment" /> No</label>
            </div>
          </div>
          <div class="confirm-row">
            <span class="confirm-question">Has budget provision been confirmed?</span>
            <div class="radio-group">
              <label class="radio-label"><input type="radio" :value="true" v-model="form.budget_provision_confirmed" /> Yes</label>
              <label class="radio-label"><input type="radio" :value="false" v-model="form.budget_provision_confirmed" /> No</label>
            </div>
          </div>
          <div class="confirm-row">
            <span class="confirm-question">Is the proposed compensation within the approved pay band?</span>
            <div class="radio-group">
              <label class="radio-label"><input type="radio" :value="true" v-model="form.is_within_approved_payband" /> Yes</label>
              <label class="radio-label"><input type="radio" :value="false" v-model="form.is_within_approved_payband" /> No</label>
            </div>
          </div>
        </div>
        <div v-if="needsBudgetExplanation" class="form-row" style="margin-top:14px">
          <label class="form-label">Budget / Confirmation Explanation <span class="req">*</span></label>
          <textarea class="form-input" rows="3" v-model="form.budget_confirmation_explanation" placeholder="Explain any 'No' answers above…"></textarea>
        </div>
      </div>

      <!-- ── Section E: Job Requirements ───────────────────────────── -->
      <div class="form-section">
        <p class="section-title">Section E — Job Requirements</p>
        <div class="form-cols-3">
          <div class="form-row">
            <label class="form-label">Minimum Academic Qualification</label>
            <select class="form-input" v-model="form.min_academic_qualification">
              <option value="">— select —</option>
              <option value="SSCE">SSCE / WAEC</option>
              <option value="OND">OND</option>
              <option value="HND">HND</option>
              <option value="BSc">BSc / BA</option>
              <option value="MSc">MSc / MBA</option>
              <option value="PhD">PhD</option>
            </select>
          </div>
          <div class="form-row">
            <label class="form-label">Professional Certification</label>
            <input type="text" class="form-input" v-model="form.professional_certificate" placeholder="e.g. ACCA, CIPM, PMP" />
          </div>
          <div class="form-row">
            <label class="form-label">Minimum Years of Experience</label>
            <input type="number" class="form-input" v-model.number="form.min_years_experience" min="0" placeholder="0" />
          </div>
        </div>
        <div class="form-row">
          <label class="form-label">NYSC Requirement</label>
          <div class="radio-group">
            <label class="radio-label"><input type="radio" value="required" v-model="form.nysc_requirement" /> Required</label>
            <label class="radio-label"><input type="radio" value="exempted" v-model="form.nysc_requirement" /> Exempted</label>
            <label class="radio-label"><input type="radio" value="not_applicable" v-model="form.nysc_requirement" /> Not Applicable</label>
          </div>
        </div>
        <div class="form-row">
          <label class="form-label">Relevant Sectors / Industries</label>
          <input type="text" class="form-input" v-model="form.relevant_sectors" placeholder="e.g. Banking, FMCG, Telecoms" />
        </div>
        <div class="form-row">
          <label class="form-label">Key Competencies (select all that apply)</label>
          <div class="checkbox-grid">
            <label v-for="c in competencyOptions" :key="c.value" class="checkbox-label">
              <input type="checkbox" :value="c.value" v-model="form.key_competence_arr" />
              <span>{{ c.label }}</span>
            </label>
          </div>
        </div>
      </div>

      <!-- ── Section F: Recruitment Approach ───────────────────────── -->
      <div class="form-section">
        <p class="section-title">Section F — Recruitment Approach</p>
        <div class="confirm-grid">
          <div class="confirm-row">
            <span class="confirm-question">Will this be open for internal applications?</span>
            <div class="radio-group">
              <label class="radio-label"><input type="radio" :value="true" v-model="form.is_internal_recruitment" /> Yes</label>
              <label class="radio-label"><input type="radio" :value="false" v-model="form.is_internal_recruitment" /> No</label>
            </div>
          </div>
          <div class="confirm-row">
            <span class="confirm-question">Will this be open for external applications?</span>
            <div class="radio-group">
              <label class="radio-label"><input type="radio" :value="true" v-model="form.is_external_recruitment" /> Yes</label>
              <label class="radio-label"><input type="radio" :value="false" v-model="form.is_external_recruitment" /> No</label>
            </div>
          </div>
          <div class="confirm-row">
            <span class="confirm-question">Is the compensation package approved?</span>
            <div class="radio-group">
              <label class="radio-label"><input type="radio" :value="true" v-model="form.compensation_approved" /> Yes</label>
              <label class="radio-label"><input type="radio" :value="false" v-model="form.compensation_approved" /> No</label>
            </div>
          </div>
        </div>

        <div class="form-row" style="margin-top:14px">
          <label class="form-label">Primary Recruitment Channel</label>
          <div class="radio-group radio-group--wrap">
            <label class="radio-label"><input type="radio" value="internal_job_board" v-model="form.recruitment_channel" /> Internal Job Board</label>
            <label class="radio-label"><input type="radio" value="recruitment_agency" v-model="form.recruitment_channel" /> Recruitment Agency</label>
            <label class="radio-label"><input type="radio" value="job_boards" v-model="form.recruitment_channel" /> Online Job Boards</label>
            <label class="radio-label"><input type="radio" value="referrals" v-model="form.recruitment_channel" /> Employee Referrals</label>
            <label class="radio-label"><input type="radio" value="linkedin" v-model="form.recruitment_channel" /> LinkedIn / Social</label>
          </div>
        </div>

        <div v-if="form.recruitment_channel === 'recruitment_agency'" class="form-row">
          <label class="form-label">Approved Agency Name</label>
          <input type="text" class="form-input" v-model="form.compensation_approve_firm" placeholder="Name of approved recruitment firm" />
        </div>
      </div>

      <!-- ── Action Buttons ─────────────────────────────────────────── -->
      <div class="action-bar">
        <button class="btn-outline" @click="goBack">Cancel</button>
        <div class="action-bar-right">
          <button v-if="!isEdit" class="btn-secondary" :disabled="saving" @click="submit('draft')">
            <font-awesome-icon icon="save" /> {{ saving === 'draft' ? 'Saving…' : 'Save as Draft' }}
          </button>
          <button class="btn-primary" :disabled="saving" @click="submit('pending_approval')">
            <font-awesome-icon icon="paper-plane" /> {{ saving === 'pending_approval' ? 'Submitting…' : (isEdit ? 'Save & Submit' : 'Submit for Approval') }}
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'JobRequisitionForm',

  data() {
    return {
      loading:       false,
      saving:        false,
      reqRef:        '',

      // Lookups
      departments:   [],
      positions:     [],
      locations:     [],
      payGrades:     [],
      approvedPlans: [],
      planRoles:     [],

      // WFP linkage state
      useWorkforcePlan: false,
      selectedRoleId:   '',

      // Form model
      form: {
        // WFP linkage
        workforce_plan_id:   '',
        no_plan_justification: '',

        // Section A
        date_of_request:           '',
        department_id:             '',
        departmental_cost_centre:  '',
        company_location_id:       '',
        employment_type:           '',
        recruitment_type:          '',
        recruitment_reason:        '',
        exiting_staff:             '',
        proposed_start_date:       '',

        // Section B
        position_id:           '',
        job_code:              '',
        pay_grade_id:          '',
        reporting_position_id: '',
        supervisory:           false,
        position_count:        1,
        work_arrangement:      '',

        // Section C (arrays for UI, serialised to string on submit)
        justification_arr:   [],
        justification_detail: '',

        // Section D
        is_approved_workforce_plan:       null,
        is_within_approved_establishment: null,
        budget_provision_confirmed:       null,
        is_within_approved_payband:       null,
        budget_confirmation_explanation:  '',

        // Section E
        min_academic_qualification: '',
        professional_certificate:   '',
        nysc_requirement:           '',
        min_years_experience:       '',
        relevant_sectors:           '',
        key_competence_arr:         [],

        // Section F
        is_internal_recruitment:  null,
        is_external_recruitment:  null,
        recruitment_channel:      '',
        compensation_approved:    null,
        compensation_approve_firm: '',
      },

      justificationOptions: [
        { value: 'new_business_requirement', label: 'New business requirement / growth' },
        { value: 'critical_operational_gap', label: 'Critical operational gap' },
        { value: 'backfill_departing',       label: 'Backfill for departing employee' },
        { value: 'specialized_skills',       label: 'Specialized skills required' },
        { value: 'regulatory_compliance',    label: 'Regulatory / compliance requirement' },
        { value: 'project_based',            label: 'Project-based need' },
      ],

      competencyOptions: [
        { value: 'leadership',      label: 'Leadership & People Management' },
        { value: 'communication',   label: 'Communication & Presentation' },
        { value: 'problem_solving', label: 'Problem Solving & Critical Thinking' },
        { value: 'technical',       label: 'Technical / Functional Skills' },
        { value: 'teamwork',        label: 'Teamwork & Collaboration' },
      ],
    };
  },

  computed: {
    reqId() { return this.$route?.params?.reqId; },
    isEdit() { return !!this.reqId; },

    selectedRole() {
      return this.planRoles.find(r => r.id === this.selectedRoleId) || null;
    },

    needsBudgetExplanation() {
      return this.form.is_approved_workforce_plan === false
          || this.form.is_within_approved_establishment === false
          || this.form.budget_provision_confirmed === false
          || this.form.is_within_approved_payband === false;
    },
  },

  mounted() {
    this.form.date_of_request = new Date().toISOString().substring(0, 10);
    Promise.all([
      this.fetchDepartments(),
      this.fetchPositions(),
      this.fetchLocations(),
      this.fetchPayGrades(),
      this.fetchApprovedPlans(),
    ]).then(() => {
      if (this.isEdit) this.loadRequisition();
    });
  },

  methods: {
    // ── Lookups ───────────────────────────────────────────────────────
    async fetchDepartments() {
      try { 
        const { data } = await axios.get('/payroll/department/active'); this.departments = data.data || []; 
      } catch {/* silent */}
    },
    async fetchPositions() {
      try { 
        const { data } = await axios.get('/payroll/position/active'); this.positions = data.data || []; 
      } catch {/* silent */}
    },
    async fetchLocations() {
      try { 
        const { data } = await axios.get('/admin/location/active'); this.locations = data.data || []; 
      } catch {/* silent */}
    },
    async fetchPayGrades() {
      try { 
        const { data } = await axios.get('/payroll/paygrade/active'); this.payGrades = data.data || []; 
      } catch {/* silent */}
    },
    async fetchApprovedPlans() {
      try { 
        const { data } = await axios.get('/hr/recruitment/workforce-plan', { params: { status: 'approved', length: 100 } }); this.approvedPlans = data.data?.data || data.data || []; 
      } catch {/* silent */}
    },
    async fetchPlanRoles(planId) {
      try {
        const { data } = await axios.get(`/hr/recruitment/workforce-plan/${planId}`);
        const plan = data.data;
        this.planRoles = plan?.roles || [];
        return plan || null;
      } catch {
        this.planRoles = [];
        return null;
      }
    },

    // ── Load for edit ─────────────────────────────────────────────────
    async loadRequisition() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/hr/recruitment/requisition/${this.reqId}`);
        const r = data.data;
        this.reqRef = r.reference;

        this.useWorkforcePlan = !!r.workforce_plan_id;
        if (r.workforce_plan_id) {
          await this.fetchPlanRoles(r.workforce_plan_id);
        }

        this.form = {
          workforce_plan_id:         r.workforce_plan_id || '',
          no_plan_justification:     r.no_plan_justification || '',
          date_of_request:           r.date_of_request?.substring(0,10) || '',
          department_id:             r.department_id || '',
          departmental_cost_centre:  r.departmental_cost_centre || '',
          company_location_id:       r.company_location_id || '',
          employment_type:           r.employment_type || '',
          recruitment_type:          r.recruitment_type || '',
          recruitment_reason:        r.recruitment_reason || '',
          exiting_staff:             r.exiting_staff || '',
          proposed_start_date:       r.proposed_start_date?.substring(0,10) || '',
          position_id:               r.position_id || '',
          job_code:                  r.job_code || '',
          pay_grade_id:              r.pay_grade_id || '',
          reporting_position_id:     r.reporting_position_id || '',
          supervisory:               r.supervisory ?? false,
          position_count:            r.position_count || 1,
          work_arrangement:          r.work_arrangement || '',
          justification_arr:         r.justification ? r.justification.split(',').filter(Boolean) : [],
          justification_detail:      r.justification_detail || '',
          is_approved_workforce_plan:       r.is_approved_workforce_plan ?? null,
          is_within_approved_establishment: r.is_within_approved_establishment ?? null,
          budget_provision_confirmed:        r.budget_provision_confirmed ?? null,
          is_within_approved_payband:        r.is_within_approved_payband ?? null,
          budget_confirmation_explanation:   r.budget_confirmation_explanation || '',
          min_academic_qualification: r.min_academic_qualification || '',
          professional_certificate:   r.professional_certificate || '',
          nysc_requirement:           r.nysc_requirement || '',
          min_years_experience:       r.min_years_experience || '',
          relevant_sectors:           r.relevant_sectors || '',
          key_competence_arr:         r.key_competence ? r.key_competence.split(',').filter(Boolean) : [],
          is_internal_recruitment:  r.is_internal_recruitment ?? null,
          is_external_recruitment:  r.is_external_recruitment ?? null,
          recruitment_channel:      r.recruitment_channel || '',
          compensation_approved:    r.compensation_approved ?? null,
          compensation_approve_firm: r.compensation_approve_firm || '',
        };
      } catch (e) {
        this.$toast?.error('Failed to load requisition');
      } finally {
        this.loading = false;
      }
    },

    // ── WFP Handlers ─────────────────────────────────────────────────
    onWFPToggle() {
      if (!this.useWorkforcePlan) {
        this.form.workforce_plan_id = '';
        this.selectedRoleId = '';
        this.planRoles = [];
      }
    },
    async onPlanChange() {
      this.selectedRoleId = '';
      this.planRoles = [];
      if (!this.form.workforce_plan_id) return;
      const plan = await this.fetchPlanRoles(this.form.workforce_plan_id);
      if (!plan) return;
      // Prefill plan-level fields
      if (plan.department_id)        this.form.department_id        = plan.department_id;
      if (plan.company_location_id)  this.form.company_location_id  = plan.company_location_id;
      if (plan.category)             this.form.employment_type      = plan.category;
      if (plan.start_date)           this.form.proposed_start_date  = plan.start_date.substring(0, 10);
    },
    onRoleChange() {
      const role = this.selectedRole;
      if (!role) return;
      // Auto-populate Section B from role
      this.form.position_id     = role.position_id || this.form.position_id;
      this.form.pay_grade_id    = role.pay_grade_id || this.form.pay_grade_id;
      this.form.position_count  = role.position_count || 1;
      this.form.employment_type = role.employment_type || this.form.employment_type;
    },

    // ── Validation ────────────────────────────────────────────────────
    validate(status) {
      if (!this.form.position_id) { this.$toast?.error('Job Title / Position is required'); return false; }
      if (!this.form.job_code) { this.$toast?.error('Job Code is required'); return false; }
      if (!this.form.department_id) { this.$toast?.error('Requesting Department is required'); return false; }
      if (!this.useWorkforcePlan && !this.form.no_plan_justification) {
        this.$toast?.error('Justification is required when no workforce plan is selected'); return false;
      }
      if (status === 'pending_approval') {
        if (!this.form.employment_type) { this.$toast?.error('Employment Type is required for submission'); return false; }
        if (!this.form.position_count || this.form.position_count < 1) { this.$toast?.error('Number of positions must be at least 1'); return false; }
      }
      return true;
    },

    // ── Save/Submit ───────────────────────────────────────────────────
    async submit(status) {
      if (!this.validate(status)) return;
      this.saving = status;
      try {
        const payload = {
          ...this.form,
          justification:     this.form.justification_arr.join(','),
          key_competence:    this.form.key_competence_arr.join(','),
          workforce_plan_id: this.useWorkforcePlan ? this.form.workforce_plan_id : '',
          status,
        };
        delete payload.justification_arr;
        delete payload.key_competence_arr;

        if (this.isEdit) {
          await axios.put(`/hr/recruitment/requisition/${this.reqId}`, payload);
        } else {
          await axios.post('/hr/recruitment/requisition', payload);
        }

        this.$toast?.success(status === 'draft' ? 'Draft saved' : 'Submitted for approval');
        this.$router.push(this.$url('/hr/recruitment'));
      } catch (e) {
        this.$toast?.error(e.response?.data?.message || 'Save failed');
      } finally {
        this.saving = false;
      }
    },

    goBack() {
      this.$router.push(this.$url('/hr/recruitment'));
    },

    // ── Helpers ───────────────────────────────────────────────────────
    fmt(v) { return v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'; },
    ucFirst(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : s; },
  },
};
</script>

<style scoped>
/* Page Header */
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
.page-header-left { display:flex; align-items:flex-start; gap:14px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.back-btn    { display:inline-flex; align-items:center; gap:6px; background:#f0f4f8; border:1px solid #dde5f0; border-radius:8px; padding:0 12px; height:36px; font-size:12px; font-weight:600; color:#4a5e78; cursor:pointer; white-space:nowrap; }
.back-btn:hover { background:#e4eaf2; }

/* Form sections */
.form-section { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:22px 24px; margin-bottom:16px; }
.section-head { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; margin-bottom:16px; }
.section-title{ font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 16px; padding-bottom:10px; border-bottom:1px solid #f0f4f8; }
.section-head .section-title { margin-bottom:0; border-bottom:none; padding-bottom:0; }

/* Form layout */
.form-cols-3 { display:grid; grid-template-columns:repeat(3, 1fr); gap:14px; }
.form-row    { display:flex; flex-direction:column; gap:5px; }
.form-label  { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; background:#fff; }
.form-input:focus { border-color:#004AAD; }
.form-input:disabled { background:#f8fafc; color:#7a90b0; cursor:not-allowed; }
.req { color:#dc2626; }

/* Toggle */
.toggle-label { display:flex; align-items:center; gap:10px; cursor:pointer; font-size:13px; font-weight:600; color:#1a2b45; }
.toggle-label input[type="checkbox"] { display:none; }
.toggle-track { position:relative; width:42px; height:22px; background:#dde5f0; border-radius:11px; transition:background .2s; flex-shrink:0; }
.toggle-track::after { content:''; position:absolute; top:3px; left:3px; width:16px; height:16px; background:#fff; border-radius:50%; transition:transform .2s; box-shadow:0 1px 3px rgba(0,0,0,.15); }
.toggle-label input:checked + .toggle-track { background:#004AAD; }
.toggle-label input:checked + .toggle-track::after { transform:translateX(20px); }

/* WFP grid */
.wfp-grid { display:flex; flex-direction:column; gap:14px; }

/* Breakdown card */
.breakdown-card { display:flex; gap:0; border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; }
.breakdown-item { flex:1; padding:12px 16px; border-right:1px solid #e4eaf2; }
.breakdown-item:last-child { border-right:none; }
.breakdown-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; display:block; margin-bottom:4px; }
.breakdown-value { font-size:14px; font-weight:700; color:#1a2b45; }

/* Info box */
.info-box { display:flex; align-items:flex-start; gap:10px; padding:12px 14px; border-radius:8px; font-size:13px; }
.info-box--blue { background:#dbeafe; border:1px solid #93c5fd; color:#1e40af; }
.info-box--warn { background:#fef9c3; border:1px solid #fde047; color:#854d0e; }
.info-box p { margin:0; }

/* Checkboxes */
.checkbox-grid { display:grid; grid-template-columns:repeat(2, 1fr); gap:10px; }
.checkbox-label { display:flex; align-items:flex-start; gap:8px; font-size:13px; color:#1a2b45; cursor:pointer; line-height:1.4; }
.checkbox-label input[type="checkbox"] { margin-top:2px; accent-color:#004AAD; }

/* Confirmation grid */
.confirm-grid { display:flex; flex-direction:column; gap:0; border:1px solid #e4eaf2; border-radius:10px; overflow:hidden; }
.confirm-row  { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; padding:13px 16px; border-bottom:1px solid #f0f4f8; }
.confirm-row:last-child { border-bottom:none; }
.confirm-question { font-size:13px; color:#1a2b45; flex:1; }

/* Radio group */
.radio-group { display:flex; gap:20px; padding:4px 0; flex-wrap:wrap; }
.radio-group--wrap { gap:12px 20px; }
.radio-label { display:flex; align-items:center; gap:6px; font-size:13px; color:#1a2b45; cursor:pointer; }
.radio-label input[type="radio"] { accent-color:#004AAD; }

/* Actions */
.action-bar { display:flex; align-items:center; justify-content:space-between; padding:20px 0 8px; }
.action-bar-right { display:flex; gap:10px; }
.btn-primary   { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; }
.btn-primary:hover:not(:disabled) { background:#003b8e; }
.btn-primary:disabled { opacity:.6; cursor:not-allowed; }
.btn-secondary { display:inline-flex; align-items:center; gap:6px; background:#f0f4f8; color:#1a2b45; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }
.btn-secondary:hover:not(:disabled) { background:#e4eaf2; }
.btn-secondary:disabled { opacity:.6; cursor:not-allowed; }
.btn-outline   { display:inline-flex; align-items:center; gap:6px; background:#fff; color:#1a2b45; font-size:13px; font-weight:600; padding:0 16px; height:38px; border-radius:8px; border:1px solid #dde5f0; cursor:pointer; }

/* Skeleton */
.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; margin-bottom:1px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

@media (max-width: 768px) {
  .form-cols-3 { grid-template-columns:1fr 1fr; }
  .checkbox-grid { grid-template-columns:1fr; }
  .breakdown-card { flex-direction:column; }
  .breakdown-item { border-right:none; border-bottom:1px solid #e4eaf2; }
  .breakdown-item:last-child { border-bottom:none; }
}
@media (max-width: 480px) {
  .form-cols-3 { grid-template-columns:1fr; }
}
</style>
